import { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import logoUrl from "@assets/logo.png";

interface BrandLogoProps {
  size?: "sm" | "md" | "lg" | "xl" | "2xl";
  showText?: boolean;
  className?: string;
  animated?: boolean;
}

const sizes = {
  sm: { img: "h-10", text: "text-lg" },
  md: { img: "h-12", text: "text-xl" },
  lg: { img: "h-16", text: "text-2xl" },
  xl: { img: "h-20", text: "text-3xl" },
  "2xl": { img: "h-24", text: "text-4xl" },
};

export function BrandLogo({ size = "md", showText = true, className = "", animated = true }: BrandLogoProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const underlineRef = useRef<HTMLDivElement>(null);
  const sizeConfig = sizes[size];

  useGSAP(() => {
    if (!animated || !containerRef.current) return;

    // Gentle float on the logo image
    if (imgRef.current) {
      gsap.to(imgRef.current, {
        y: -3,
        duration: 3,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }

    // Glow pulse
    if (glowRef.current) {
      gsap.to(glowRef.current, {
        scale: 1.15,
        opacity: 0.5,
        duration: 3,
        ease: "sine.inOut",
        repeat: -1,
        yoyo: true,
      });
    }

    // Underline sweep
    if (underlineRef.current) {
      gsap.fromTo(underlineRef.current,
        { width: "0%" },
        { width: "100%", duration: 2, ease: "sine.inOut", repeat: -1, yoyo: true, repeatDelay: 1 }
      );
    }

    // Hover scale on container
    const el = containerRef.current;
    const onEnter = () => gsap.to(el, { scale: 1.03, duration: 0.3, ease: "power2.out" });
    const onLeave = () => gsap.to(el, { scale: 1, duration: 0.3, ease: "power2.out" });
    el.addEventListener("mouseenter", onEnter);
    el.addEventListener("mouseleave", onLeave);
    return () => {
      el.removeEventListener("mouseenter", onEnter);
      el.removeEventListener("mouseleave", onLeave);
    };
  }, { scope: containerRef });

  if (!animated) {
    return (
      <div className={`flex items-center gap-3 ${className}`}>
        <img src={logoUrl} alt="FillSign" className={`${sizeConfig.img} object-contain`} />
        {showText && (
          <span className={`font-bold ${sizeConfig.text}`} style={{ color: "var(--fs-forest)" }}>FillSign</span>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={`flex items-center gap-3 ${className}`}>
      <div className="relative">
        {/* Glow behind logo */}
        <div
          ref={glowRef}
          className="absolute inset-0 rounded-full blur-xl pointer-events-none"
          style={{ background: "radial-gradient(circle, var(--fs-sage) 0%, transparent 70%)", opacity: 0.3 }}
        />
        <img ref={imgRef} src={logoUrl} alt="FillSign" className={`${sizeConfig.img} object-contain relative z-10 drop-shadow-sm`} />
      </div>
      {showText && (
        <div className="relative">
          <span className={`font-bold ${sizeConfig.text}`} style={{ color: "var(--fs-forest)" }}>FillSign</span>
          <div
            ref={underlineRef}
            className="absolute -bottom-0.5 left-0 h-0.5 rounded-full"
            style={{ background: "linear-gradient(90deg, var(--fs-sage), var(--fs-amber))", width: "0%" }}
          />
        </div>
      )}
    </div>
  );
}

export function BrandIcon({ size = "md", className = "", animated = false }: Omit<BrandLogoProps, "showText">) {
  const sizeConfig = sizes[size];
  return <img src={logoUrl} alt="FillSign" className={`${sizeConfig.img} object-contain ${className}`} />;
}
